import { STORAGE_KEYS } from '@shared/constants';
import { browserStorage } from '@/lib/ui-prefs';

/**
 * The team a brand-new tab opens on (T-155). Each tab keeps its own session in sessionStorage
 * (the auth store), so two tabs can work in two teams. localStorage holds only this team id,
 * written on an explicit team choice and when a tab gains focus. This is the only module that
 * touches it.
 */

type KeyStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** localStorage, or null where there is none (Node in tests) or it throws (some private modes). */
function sharedStore(): KeyStore | null {
  return browserStorage('local');
}

export function readLastTeam(store: KeyStore | null = sharedStore()): string | null {
  try {
    return store?.getItem(STORAGE_KEYS.LAST_TEAM) || null;
  } catch {
    return null;
  }
}

export function rememberTeamChoice(
  teamId: string | null | undefined,
  store: KeyStore | null = sharedStore()
): void {
  if (!teamId) return;
  try {
    store?.setItem(STORAGE_KEYS.LAST_TEAM, teamId);
  } catch {
    // Full or blocked storage: new tabs fall back to the server's choice.
  }
}

export function forgetLastTeam(store: KeyStore | null = sharedStore()): void {
  try {
    store?.removeItem(STORAGE_KEYS.LAST_TEAM);
  } catch {
    // Nothing to forget.
  }
}

/** The team to ask the server for: this tab's own, else the one a new tab should open on. */
export function preferredTeamForThisTab(
  tabTeamId: string | null | undefined,
  store: KeyStore | null = sharedStore()
): string | null {
  return tabTeamId || readLastTeam(store) || null;
}

/** Where every tab used to share one session, token included. */
const LEGACY_AUTH_STORAGE = 'auth-storage';
const LEGACY_SHARED_KEYS = [
  LEGACY_AUTH_STORAGE,
  STORAGE_KEYS.AUTH_TOKEN,
  STORAGE_KEYS.USER,
  'auth_token',
];

function legacyTeamId(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const id = JSON.parse(raw)?.state?.user?.active_team?.id;
    return typeof id === 'string' && id ? id : null;
  } catch {
    return null;
  }
}

/**
 * Run once at load: keep the shared session's team as the new-tab default, then delete the shared
 * session (a token in localStorage is what made tabs follow each other). Kept permanently: it is
 * cheap, and the dev-screenshot and extension e2e seeds still write the old key.
 */
export function migrateLegacySharedAuth(store: KeyStore | null = sharedStore()): void {
  if (!store) return;
  try {
    if (!readLastTeam(store)) {
      rememberTeamChoice(legacyTeamId(store.getItem(LEGACY_AUTH_STORAGE)), store);
    }
    for (const key of LEGACY_SHARED_KEYS) store.removeItem(key);
  } catch {
    // Blocked storage: nothing was shared there either.
  }
}
